/*
Copyright 2025 TekNix Corporation

This software is proprietary and confidential to TekNix Corporation.
All rights reserved. No part of this software may be reproduced, distributed,
or transmitted in any form or by any means, including photocopying, recording,
or other electronic or mechanical methods, without the prior written permission
of TekNix Corporation, except in the case of brief quotations embodied in
critical reviews and certain other noncommercial uses permitted by copyright law.

For permission requests, write to TekNix Corporation at the address below:
TekNix Corporation
Legal Department

UNAUTHORIZED REPRODUCTION OR DISTRIBUTION IS STRICTLY PROHIBITED.
*/

import type { ICreateClientOpts, IStartClientOpts, IUserDirectoryResponse, UserInfo } from './@types/client';
import type { EmptyObject } from './@types/common';
import type { ClientEventHandlerMap, EmittedEvents } from './@types/emitMapper';
import type { Comment, IContent, IEvent, ServerCommentRes, ServerMessageRes, TimelineEvents } from './@types/event';
import type { AccountDataEvents, RoomMessageEventContent } from './@types/events';
import type { WrappedReceipt } from './@types/read_receipts';
import type { IRedactOpts, LikeCommentResponse, LikePostResponse } from './@types/request';
import type { Body, CursorParams, HttpResponse, IHttpOpts, QueryDict, ResponseCursor } from './http';
import type { Beacon, BeaconIdentifier } from './models/beacon/beacon';
import type { EventMapper, MapperOpts } from './models/event/event-mapper';
import type { EventTimelineSet } from './models/event/event-timeline-set';
import type { RealtimeMessagePayload } from './models/realtime/interface';
import type { Room } from './models/room/room';
import type { Contact } from './models/user/contact';
import type { XHubChatScheduler } from './scheduler/scheduler';
import type { IStore, IStoredClientOpts } from './store/interfaces/store';
import type { SlidingSync } from './sync';
import type { IRoomEvent, IStateEvent, RoomData, RoomsResponse, SlidingSyncResponse } from './sync/interface';
import type { Feature, ServerSupport } from './utils/feature';

import type { Logger } from './utils/logger';
import type { Direction, IPaginateOpts, ISendEventResponse, Optional, SendDelayedEventRequestOpts, SendDelayedEventResponse } from './xhubchat';
import { ClientEvent, EventStatus, EventType, MsgType, XHubChatEventEvent } from './@types/event';
import { ReceiptType } from './@types/read_receipts';
import { NotificationCountType } from './@types/room';
import { TypedReEmitter } from './emit/ReEmitter';
import { TypedEventEmitter } from './emit/typed-event-emitter';
import { ClientPrefix, Method, XHubChatHttpApi } from './http';
import { XHubChatEvent } from './models/event/event';
import { eventMapperFor } from './models/event/event-mapper';
import { Filter } from './models/filter/filter';
import { threadIdForReceipt } from './models/read-receipt';
import { CentrifugeRealtime } from './models/realtime/centrifuge-realtime';
import { RealtimeEvent, RealtimeTransactionType } from './models/realtime/interface';
import { THREAD_RELATION_TYPE } from './models/thread/thread';
import { ContactsManager } from './models/user/contacts-manager';
import { User } from './models/user/user';
import { StubStore } from './store/stub';
import { SlidingSyncState, SyncState } from './sync/interface';
// eslint-disable-next-line sort-imports
import { deepCompare, encodeUri, MapWithDefault, retryNetworkOperation, secureRandomString } from './utils';
import { makeTextMessage } from './utils/content-helper';
import { logger } from './utils/logger';
import { EventTimeline, KnownMembership, RoomCategory, XHubChatError } from './xhubchat';

export type Store = IStore;

// We're using this constant for methods overloading and inspect whether a variable
// contains an eventId or not. This was required to ensure backwards compatibility
// of methods for threads
// Probably not the most graceful solution but does a good enough job for now
export const THREAD_PREFIX = '$';

export class XHubChatClient extends TypedEventEmitter<EmittedEvents, ClientEventHandlerMap> {
  private readonly logger: Logger;
  /**
   * The base URL of the backend server this client is connected to.
   */
  public baseUrl: string;
  public deviceId: string | null;
  protected sessionId: string;
  public scheduler?: XHubChatScheduler;

  private _store!: Store;
  public reEmitter = new TypedReEmitter<EmittedEvents, ClientEventHandlerMap>(this);
  public http: XHubChatHttpApi<IHttpOpts & { onlyData: true }>; // XXX: Intended private, used in code.
  public credentials: { userId: string | null } & UserInfo;

  protected notifTimelineSet: EventTimelineSet | null = null;

  public readonly beacons = new Map<BeaconIdentifier, Beacon>();

  // receipts should clobber based on receipt_type and user_id pairs hence
  // the form of this structure. This is sub-optimal for the exposed APIs
  // which pass in an event ID and get back some receipts, so we also store
  // a pre-cached list for this purpose.
  // Map: receipt type → user Id → receipt
  private receipts = new MapWithDefault<string, Map<string, [
    realReceipt: WrappedReceipt | null,
    syntheticReceipt: WrappedReceipt | null,
  ]>>(() => new Map());

  protected txnCtr = 0;

  protected clientOpts?: IStoredClientOpts;

  protected syncApi?: SlidingSync;

  public clientRunning = false;

  public canSupport = new Map<Feature, ServerSupport>();

  private contactsManager?: ContactsManager;

  /**
   * The cursor to fetch next data from server and cache
   *  - fromServer: The cursor returned from server - The cursor from server have another format with cache.
   *  - fromCache: The cursor used to fetch from cache - The cursor from cache is the last room ID we have in cache.
   */
  private nextRoomsToken: {
    fromServer: string | null;
    fromCache: string | null;
  } | null = null;

  // ====== realtime ======
  private _realtime?: CentrifugeRealtime;
  private realtimeOpts?: ICreateClientOpts['realtime'];

  public constructor(opts: ICreateClientOpts) {
    super();

    // If a custom logger is provided, use it. Otherwise, default to the global one in logger.ts.
    this.logger = opts.logger ?? logger;
    this.store = opts.store || new StubStore();
    this.deviceId = opts.deviceId || null;
    this.sessionId = secureRandomString(10);
    this.baseUrl = opts.baseUrl;

    this.realtimeOpts = {
      token: opts.accessToken, // By default use accessToken for initial connection
      ...opts.realtime, // if token provided in realtime opts, it will override accessToken
    };

    const userId = opts.userId || null;
    this.credentials = { userId, ...opts.userInfo };

    this.contactsManager = new ContactsManager(this);

    this.http = new XHubChatHttpApi(this as ConstructorParameters<typeof XHubChatHttpApi>[0], {
      fetchFn: opts.fetchFn,
      baseUrl: opts.baseUrl,
      accessToken: opts.accessToken,
      tokenRefreshFunction: opts.tokenRefreshFunction,
      prefix: ClientPrefix.V1,
      onlyData: true,
      extraParams: opts.queryParams,
      localTimeoutMs: opts.localTimeoutMs,
      useAuthorizationHeader: opts.useAuthorizationHeader,
      logger: this.logger,
      clientParams: opts.clientParams,
    });

    // ============ Scheduler ============
    this.scheduler = opts.scheduler;
    if (this.scheduler) {
      this.scheduler.setProcessFunction(async (eventToSend: XHubChatEvent) => {
        const room = this.getRoom(eventToSend.getRoomId());

        if (eventToSend.status !== EventStatus.SENDING) {
          logger.debug(`Scheduler processing event ${eventToSend.getId()} which is not in the SENDING state`);
          this.updatePendingEventStatus(room, eventToSend, EventStatus.SENDING);
        }
        const res = await this.sendEventHttpRequest(eventToSend);
        if (room) {
          if (room.roomData.category === RoomCategory.ChatRoom) {
            // ensure we update pending event before the next scheduler run so that any listeners to event id
            // updates on the synchronous event emitter get a chance to run first.
            room.updatePendingEvent(eventToSend, EventStatus.SENT, res.data.event_id || res.data.key);
          } else {
            room.updatePendingEvent(eventToSend, EventStatus.SENT, res.data.id);

            const comment = res.data as unknown as Comment;

            const roomId = comment.post_id ?? ((comment as LikePostResponse).id.split('_post_')?.[0] || 'no-room-id');
            const newData = {
              key: eventToSend.getTxnId()!,
              detail: {
                content: comment.content,
                created_at: comment.created_at,
                id: comment.id.replace('_post_content', ''), // Remove suffix to get room ID
                participants_readed: null,
                room_id: roomId, // Pass post_id for like event
                sender_id: comment.commenter_id,
                timestamp: comment.created_at,
                type: 'text',
                updated_at: comment.updated_at,
                ...(comment.reply_to_comment && { // If reply_to_comment exists, add m.in_reply_to relation
                  'm.relates_to': {
                    event_id: comment.parent_comment_id || comment.reply_to_comment_id,
                    ...(comment.reply_to_comment_id
                      && comment.parent_comment_id !== comment.reply_to_comment_id
                      && { 'm.in_reply_to': {
                        event_id: comment.reply_to_comment_id,
                      } }),
                    rel_type: 'm.thread',
                  },
                }),
              },
              message: '',
              transaction_type: comment.commenter ? RealtimeTransactionType.Comment : RealtimeTransactionType.Chat,

              // Like a post event
              ...(comment.commenter
                ? {}
                : {
                    echoReactEvent: {
                      type: (comment as LikePostResponse).id.includes('_like') ? EventType.Reaction : EventType.RoomRedaction,
                      id: (comment as LikePostResponse).id,
                      content:
                  (comment as LikePostResponse).id.includes('_like')
                    ? {
                        'm.relates_to': {
                          rel_type: 'm.annotation',
                          event_id: (comment as LikePostResponse)
                            .id
                            .replace('_post_like', '_post_content') // Remove suffix to get content event ID, if in post, to get Post ID
                            .replace('_comment_like', ''), // If in comment, remove suffix to get comment ID
                          key: '❤️',
                        },
                      }
                    : {
                        redacts: (comment as LikePostResponse)
                          .id
                          .replace('_post_unlike', '_post_like') // redact the like event
                          .replace('_comment_unlike', '_comment_like'), // If in comment, remove suffix to get like event ID
                      },
                      created_at: new Date().toISOString(),
                      sender_id: this.getUserId() || '',
                      timestamp: new Date().getTime(),
                      room_id: roomId,
                      ...((comment as LikePostResponse).id.includes('_unlike') && { // Add `redacts` redaction only for unlike event
                        redacts: (comment as LikePostResponse)
                          .id
                          .replace('_post_unlike', '_post_like')
                          .replace('_comment_unlike', '_comment_like'),
                      }),
                      unsigned: {
                        transaction_id: eventToSend.getTxnId()!,
                      },
                    } as IRoomEvent,
                  }),
            } as RealtimeMessagePayload;
            this.getRealtime()?.emit(RealtimeEvent.NewMessage, newData);
          }
        }
        return res;
      });
    }

    // ===== Listeners =====
    this.on(ClientEvent.Sync, this.fixupRoomNotifications);

    // having lots of event listeners is not unusual. 0 means "unlimited".
    this.setMaxListeners(0);
  }

  public set store(newStore: Store) {
    this._store = newStore;
    this._store.setUserCreator((userId, channel_slug) => User.createUser(userId, channel_slug, this));
  }

  public get store(): Store {
    return this._store;
  }

  /**
   * Set a new access token and retry all queued requests.
   * This should be called when the application detects token expiration and refreshes the token.
   * Use this when tokenRefreshFunction is NOT provided during client creation.
   * @param accessToken - The new access token to use for API requests
   * @example
   * ```typescript
   * client.on(HttpApiEvent.TokenExpired, async () => {
   *   const newToken = await yourAuthService.refreshToken();
   *   client.setAccessToken(newToken);
   * });
   * ```
   */
  public setAccessToken(accessToken: string): void {
    this.http.setAccessToken(accessToken);
    // Also update realtime token if realtime is active
    if (this._realtime) {
      this._realtime.refreshToken().catch((err) => {
        this.logger.error('Failed to refresh realtime token after setAccessToken:', err);
      });
    }
  }

  /**
   * Get statistics about queued requests waiting for token refresh
   */
  public getRequestQueueStats(): ReturnType<typeof this.http.getQueueStats> {
    return this.http.getQueueStats();
  }

  /**
   * Clear all queued requests
   * @param reason - Optional reason for clearing the queue
   */
  public clearRequestQueue(reason?: string): void {
    this.http.clearQueue(reason);
  }

  /**
   * High level helper method to begin syncing and poll for new events. To listen for these
   * events, add a listener for {@link ClientEvent.Event}
   * via {@link XHubChatClient#on}. Alternatively, listen for specific
   * state change events.
   * @param opts - Options to apply when syncing.
   */
  public async startClient(opts?: IStartClientOpts): Promise<void> {
    // client is already running.
    if (this.clientRunning) return;
    // Flag that the client is running
    this.clientRunning = true;

    // Create our own user object artificially (instead of waiting for sync)
    // so it's always available, even if the user is not in any rooms etc.
    const userId = this.getUserId();
    if (userId) this.store.storeUser(User.createUser(userId, userId, this));

    if (this.syncApi) {
      // This shouldn't happen since we thought the client was not running
      this.logger.error('Still have sync object whilst not running: stopping old one');
      this.syncApi.stop();
    }

    // Set up sync API
    this.clientOpts = opts;
    if (this.clientOpts?.slidingSync) {
      this.syncApi = this.clientOpts?.slidingSync;
      // Run web-socket sync
      this.syncApi.start().catch(e => this.logger.info('Sync startup aborted with an error:', e));
    }

    // start realtime after establishing sync
    if (this.realtimeOpts) {
      this.getRealtime()?.connect().catch(err => this.logger.error('Realtime connect failed', err));
    }

    await this.contactsManager?.initialize();

    await this.loadRoomList();
  }

  private mappingServerMsgToSdkMsg(message: IRoomEvent | IStateEvent): IRoomEvent {
    return {
      type: EventType.RoomMessage,
      id: message.id,
      sender_id: message.sender_id,
      timestamp: new Date(message?.timestamp).getTime(),
      content: message.content,
      created_at: message.created_at,
    } as IRoomEvent;
  }

  /**
   * Map server room data to SDK room data
   * @param data - Data from the server
   * @returns The mapped room data suitable for SlidingSyncResponse
   */
  public async mappingRoomServerToSdkRes(data: RoomsResponse): Promise<SlidingSyncResponse['rooms']> {
    const rooms = data.items || [];
    const sdkRooms: SlidingSyncResponse['rooms'] = {};
    await Promise.all(rooms.map(async (room) => {
      const lastMessage = (room.last_message && room.category === RoomCategory.ChatRoom && Object.keys(room.last_message).length > 0)
        ? [this.mappingServerMsgToSdkMsg({
            ...room.last_message,
            content: {
              msgtype: MsgType.Text,
              text: room.last_message.content || '',
            },
          })]
        : [];

      sdkRooms[room.id] = {
        ...room,
        timeline: [
          ...(
            room.category === RoomCategory.ChatRoom
              ? lastMessage
              : [{
                  type: EventType.RoomMessage,
                  id: `${room.id}_post_content`,
                  content: {
                    msgtype: MsgType.Text,
                    text: room.description || room.content || '',
                  },
                  created_at: room.created_at,
                  sender_id: room.participants?.[0] || '',
                  timestamp: new Date(room.created_at).getTime(),
                  unsigned: {
                    'age': 12345,
                    'm.relations': {
                      ...(room.likes && { 'm.annotation': {
                        chunk: [{ // no need to fill this out - This field is some react sample data
                          type: 'm.reaction',
                          sender: room.participants?.[0] || '',
                          content: { 'm.relates_to': { key: '❤️' } },
                        }],
                        count: room.likes || 0,
                        limited: true,
                      } }),
                    },
                  },
                }, ...(room.liked
                  ? [{
                      type: EventType.Reaction,
                      id: `${room.id}_post_like`,
                      content: {
                        'm.relates_to': {
                          rel_type: 'm.annotation',
                          event_id: `${room.id}_post_content`,
                          key: '❤️',
                        },
                      },
                      created_at: room.created_at,
                      sender_id: this.getUserId() || '',
                      timestamp: new Date(room.created_at).getTime(),
                      room_id: room.id,
                    } as IRoomEvent]
                  : [])]
          ),
        ],
        required_state: [
          {
            type: 'm.room.create',
            state_key: 'room.create',
            content: {
              creator: room.participants?.[0] || '',
              room_version: '1',
              type: null,
            },
            sender_id: room.participants?.[0] || '',
            timestamp: new Date(room.created_at).getTime(),
            created_at: room.created_at,
            id: `${room.id}_create`,
            unsigned: {
              age: 0,
            },
          },
          {
            type: 'm.room.name',
            state_key: 'room.name',
            content: {
              name: room.category === RoomCategory.ChatRoom ? room.name : room.title,
            },
            sender_id: room.participants?.[0] || '',
            timestamp: new Date(room.created_at).getTime(),
            created_at: room.created_at,
            id: `${room.id}_name`,
          },
          ...(room.members?.map((p) => {
            return ({
              type: 'm.room.member',
              state_key: p.channel_slug || p.slug,
              content: {
                membership: KnownMembership.Join,
                avatar_url: p?.logo,
                displayname: p?.name,
                description: p?.description,
              },
              created_at: room.created_at,
              sender_id: p.id,
              timestamp: new Date(room.created_at).getTime(),
              id: `${room.id}_member_${p}`,
              unsigned: {
                age: 0,
              },
            }) as IStateEvent;
          }) || []),
          {
            content: {
              url: room.media?.[0]?.url || '',
            },
            timestamp: new Date().getTime(),
            sender_id: room.participants?.[0] || '',
            state_key: '',
            type: 'm.room.avatar',
            id: `${room.id}_room_avatar`,
            room_id: room.id,
            created_at: new Date().toISOString(),
          },
          {
            type: 'm.room.member',
            state_key: this.getUserId() || '',
            content: {
              membership: KnownMembership.Join,
              avatar_url: this.credentials.avatarUrl,
              displayname: this.credentials.displayName,
              description: this.credentials.description,
            },
            created_at: room.created_at,
            sender_id: this.getUserId() || '',
            timestamp: new Date(room.created_at).getTime(),
            id: `${room.id}_member_${this.getUserId() || ''}`,
            unsigned: {
              age: 0,
            },
          },
        ],
        initial: true,
        bump_stamp: new Date(room.updated_at).getTime(),
        heroes: room.participants?.map(p => ({ userId: p })) || [],
        num_live: 0,
        name: room.category === RoomCategory.ChatRoom ? room.name : room.title,
        prev_batch: room.category === RoomCategory.ChatRoom
          ? (room.last_message ? room.last_message.created_at : undefined)
          : `${room.id}_post_content`, // use the post content event ID as prev_batch for non-chat rooms
        limited: true,
        message_new_count: room.category === RoomCategory.ChatRoom
          ? room.message_new_count
          : (room.is_read ? 0 : 1),
      };
    }));

    return sdkRooms;
  }

  /**
   * Convert the server response to an SDK response
   * @returns - The converted SDK response or null if there was no pending request
   */
  public async convertServerResToSdkRes(resp: RoomsResponse): Promise<SlidingSyncResponse | null> {
    const sdkResp: SlidingSyncResponse = {
      pos: resp.next_cursor || new Date().getTime().toString(),
      txn_id: resp.next_cursor || undefined,
      lists: { all_rooms: { has_next: resp.has_next } },
      rooms: await this.mappingRoomServerToSdkRes(resp),
      extensions: {
        presence: {
          [this.getUserId()!]: true,
        },
      },
    };
    return sdkResp;
  }

  /**
   * Check if we can paginate rooms from server
   * @returns True if we can paginate rooms, false otherwise
   */
  public canPaginateRooms(): boolean {
    return !!(this.nextRoomsToken && this.nextRoomsToken.fromServer);
  }

  public async paginateRooms({ limit }: { limit?: number }): Promise<void> {
    if (!this.canPaginateRooms() || !this.nextRoomsToken) return;

    try {
      const { fromCache, fromServer } = this.nextRoomsToken;

      this.logger.debug('Paginating rooms from server...');
      await this.loadRoomsFromCache({ cursor: fromCache || undefined, limit });

      await this.loadRoomsFromServer({ cursor: fromServer || undefined, limit });
    } catch (error) {
      this.logger.error('Failed to paginate rooms from server', error);
      throw error;
    }
  }

  /**
   * Access the contacts manager for this client.
   *
   * The contacts manager provides an easy way to manage and display direct message
   * contacts, search for users, and create DM rooms.
   *
   * @returns The contacts manager instance
   */
  public getContactsManager(): ContactsManager {
    if (!this.contactsManager) {
      this.contactsManager = new ContactsManager(this);
    }
    return this.contactsManager;
  }

  /**
   * Get the contact list
   * @returns The contact list
   */
  public getContactList(): Contact[] {
    return this.getContactsManager().getContacts();
  }

  /**
   * Load rooms from server (separated from WebSocket sync)
   * @returns Promise which resolves when the server has been loaded
   */
  private async loadRoomsFromServer(opts?: { cursor?: string; limit?: number }): Promise<void> {
    try {
      this.logger.debug('Loading rooms from server...');

      const _resp = await this.fetchRooms(
        { cursor: opts?.cursor, limit: opts?.limit ?? this.clientOpts?.initialSyncLimit ?? 20 },
      );
      this.nextRoomsToken = {
        fromCache: this.nextRoomsToken?.fromCache || null,
        fromServer: _resp.data.next_cursor || null,
      };

      const resp = await this.convertServerResToSdkRes(_resp.data);

      if (resp) {
        if (!opts || !opts.cursor) resp.firstLoadFromServer = true;

        // Process extensions
        this.syncApi?.onLifecycle(SlidingSyncState.RequestFinished, resp, undefined);
        await this.syncApi?.onPreExtensionsResponse(resp.extensions);

        // Process rooms
        for (const roomId in resp.rooms) {
          await this.syncApi?.onRoomData(roomId, resp.rooms[roomId]);
        }

        // Update cache
        await this.store.setRoomsData(_resp.data.items.reduce((acc, roomData) => {
          acc[roomData.id] = { ...roomData, timeline: [] }; // do not store timeline in cache
          return acc;
        }, {} as Record<string, RoomData>));

        this.syncApi?.onLifecycle(SlidingSyncState.Complete, resp);
        await this.syncApi?.onPostExtensionsResponse(resp.extensions);
      }
    } catch (error) {
      this.logger.error('Failed to load rooms from server', error);
      throw error;
    }
  }

  /**
   * Load rooms from cache (separated from WebSocket sync)
   * @returns Promise which resolves when the cache has been loaded
   */
  private async loadRoomsFromCache(opts?: { limit?: number; cursor?: string }): Promise<void> {
    return this.store.getSavedRooms(true).then(async (roomsData) => {
      if (!roomsData) return Promise.resolve();

      if (opts?.limit && roomsData.length > opts.limit && opts.limit > 0 && !opts.cursor) {
        roomsData = [...roomsData].slice(0, opts.limit);
      }

      if (opts?.cursor) {
        const index = roomsData.findIndex(r => r.id === opts.cursor);
        if (index !== -1) {
          roomsData = roomsData.slice(index + 1, index + 1 + (opts.limit || roomsData.length));
        }
      }

      this.nextRoomsToken = {
        fromCache: roomsData.length ? roomsData[roomsData.length - 1].id : null,
        fromServer: this.nextRoomsToken?.fromServer || null,
      };

      const resp = await this.convertServerResToSdkRes({ items: roomsData, has_next: false, next_cursor: null });

      if (!resp) return;
      await this.syncApi?.onPreExtensionsResponse(resp.extensions);

      for (const roomId in resp.rooms) {
        await this.syncApi?.onRoomData(roomId, resp.rooms[roomId]);
      }
      await this.syncApi?.onPostExtensionsResponse(resp.extensions);
    });
  }

  /**
   * When the app is started for the first time, it will be called to load the list of rooms
   * can be taken from IndexedDB and call the API to get the latest list
   * @returns Promise which resolves when both cache and server have been loaded
   */
  public async loadRoomList(): Promise<void> {
    // Step 1: Load from cache first (instant)
    this.loadRoomsFromCache({ limit: this.clientOpts?.initialSyncLimit ?? 20 });

    // Step 2: Fetch fresh data from server
    await this.loadRoomsFromServer({ limit: this.clientOpts?.initialSyncLimit ?? 20 });// Fetch from server
  }

  public async getThreadTimeline(timelineSet: EventTimelineSet, eventId: string): Promise<EventTimeline | undefined> {
    if (!this.supportsThreads()) {
      throw new Error('could not get thread timeline: no client support');
    }
    if (!timelineSet.room) {
      throw new Error('could not get thread timeline: not a room timeline');
    }
    if (!timelineSet.thread) {
      throw new Error('could not get thread timeline: not a thread timeline');
    }

    const params: Record<string, string | string[]> = {
      limit: '0',
    };
    if (this.clientOpts?.lazyLoadMembers) {
      params.filter = JSON.stringify(Filter.LAZY_LOADING_MESSAGES_FILTER);
    }

    return undefined;
  }

  /**
   * Get an EventTimeline for the given event
   *
   * <p>If the EventTimelineSet object already has the given event in its store, the
   * corresponding timeline will be returned. Otherwise, a /context request is
   * made, and used to construct an EventTimeline.
   * If the event does not belong to this EventTimelineSet then undefined will be returned.
   *
   * @param timelineSet -  The timelineSet to look for the event in, must be bound to a room
   * @param eventId -  The ID of the event to look for
   *
   * @returns Promise which resolves:
   *    {@link EventTimeline} including the given event
   */
  public async getEventTimeline(timelineSet: EventTimelineSet, eventId: string): Promise<Optional<EventTimeline>> {
    if (!timelineSet?.room) {
      throw new Error('getEventTimeline only supports room timelines');
    }

    if (timelineSet.getTimelineForEvent(eventId)) {
      return timelineSet.getTimelineForEvent(eventId);
    }

    const path = ('/messages/new-messages-from-point');
    const params = { point: eventId, limit: 15, room_chat_id: timelineSet.room.roomId };

    const prevRes = this.http.authedRequest<IEvent>(Method.Get, path, { ...params, order: 'pre' });
    const nextRes = this.http.authedRequest<IEvent>(Method.Get, path, { ...params, order: 'post' });

    await Promise.all([prevRes, nextRes]).then(([prev, next]) => {});
    return null;
  }

  public mapperCommentResponse = (messages: Comment[]): SlidingSyncResponse['rooms'][string]['timeline'] => {
    return messages.map((msg) => {
      // A Event is Root Thread if:
      //  - It is not a reply to another comment
      //  - It has replies
      //  - Not have parent_comment_id
      // If just condition 1 (not reply to another comment), it is just a normal comment in a mainline. And its not a thread root.

      const repliedComments = msg.replied_comments || [];
      const hasReplies = repliedComments.length > 0;
      const isRootThread = !msg.reply_to_comment && hasReplies;
      const firstReply = repliedComments[0];

      const unsignedRootThread = (isRootThread && firstReply)
        && {
          'm.thread': {
            latest_event: { // First comment in replied_comments is the latest comment
              content: {
                'm.relates_to': {
                  event_id: firstReply.parent_comment_id, // The root comment ID
                  is_falling_back: true,
                  rel_type: 'm.thread',
                  // If this comment is a reply to another comment and this not the parent comment
                  ...(firstReply.reply_to_comment_id && firstReply.reply_to_comment_id !== msg.parent_comment_id
                    && { 'm.in_reply_to': { event_id: firstReply.reply_to_comment_id } }
                  ),
                },
              },
              origin_server_ts: new Date(firstReply.updated_at).getTime(),
              room_id: firstReply.post_id,
              sender: firstReply.commenter.id,
              type: 'm.room.message',
              event_id: firstReply.id,
              user_id: firstReply.commenter.id,
              age: 409595,
            },
            count: msg.replies || repliedComments.length,
            current_user_participated: repliedComments.some(c => c.commenter?.id === this.getUserId()),
          },
        }
                  ;

      const normalEventInThreadContent = msg.parent_comment_id
        && {
          'm.relates_to': {
            rel_type: 'm.thread',
            event_id: msg.parent_comment_id,
            is_falling_back: true,
            // If this comment is a reply to another comment and this not the parent comment
            ...(msg.reply_to_comment_id && msg.reply_to_comment_id !== msg.parent_comment_id
              && { 'm.in_reply_to': { event_id: msg.reply_to_comment_id } }
            ),
          },
        };

      return {
        type: 'm.room.message',
        sender_id: msg.commenter_id,
        content: {
          text: msg.content,
          msgtype: MsgType.Text,
          ...normalEventInThreadContent,
        },
        state_key: undefined,
        id: msg.id,
        time_pin: msg.time_pin,
        timestamp: new Date(msg.updated_at).getTime(),
        event_id: msg.id,
        origin_server_ts: new Date(msg.updated_at).getTime(),
        room_id: msg.post_id,
        created_at: msg.created_at,
        unsigned: {
          'm.relations': {
            ...(msg.likes && { 'm.annotation': {
              chunk: [{ // no need to fill this out - This field is some react sample data
                type: 'm.reaction',
                sender: new Date().getTime().toString() || '',
                content: { 'm.relates_to': { key: '❤️' } },
              }],
              count: msg.likes || 0,
              limited: true,
            } }),
            ...unsignedRootThread,
          },

        },
      };
    });
  };

  /**
   * Take an EventTimeline, and back/forward-fill results.
   *
   * @param eventTimeline - timeline object to be updated
   *
   * @returns Promise which resolves to a boolean: false if there are no
   *    events and we reached either end of the timeline; else true.
   */
  public paginateEventTimeline(eventTimeline: EventTimeline, opts: IPaginateOpts): Promise<boolean> {
    const room = this.getRoom(eventTimeline.getRoomId()!);
    const threadListType = eventTimeline.getTimelineSet().threadListType;
    const thread = eventTimeline.getTimelineSet().thread;

    // TODO: we should implement a backoff (as per scrollback()) to deal more
    // nicely with HTTP errors.
    opts = opts || {};
    const backwards = opts.backwards || false;

    const dir = backwards ? EventTimeline.BACKWARDS : EventTimeline.FORWARDS;

    const token = eventTimeline.getPaginationToken(dir);
    const pendingRequest = eventTimeline.paginationRequests[dir];
    // already a request in progress - return the existing promise
    if (pendingRequest) return pendingRequest;

    let promise: Promise<boolean>;

    if (threadListType !== null) {
      return Promise.resolve(false);
    } else if (thread) {
      const room = this.getRoom(eventTimeline.getRoomId() ?? undefined);
      if (!room) {
        throw new Error(`Unknown room ${eventTimeline.getRoomId()}`);
      }

      promise = this.fetchEventsInThread(thread.id, {
        limit: opts.limit || 12,
        cursor: token || undefined,
      })
        .then(async (_res) => {
          // TODO: Remove this when have API support for pagination in threads
          const msgs = _res.data.comments;
          const mapper = this.getEventMapper();

          // Update member info when fetch from server
          this.updateRoomMemberWhenFetchFromServer(msgs, eventTimeline);

          const appendLikedByMe = (msgs: Comment[]): (IRoomEvent | IStateEvent)[] => {
            const newReactionEvents: IRoomEvent[] = [];
            msgs.forEach((msg) => {
              if (msg.liked) {
                newReactionEvents.push({
                  type: EventType.Reaction,
                  id: `${msg.id}_comment_like`,
                  content: {
                    'm.relates_to': {
                      rel_type: 'm.annotation',
                      event_id: `${msg.id}`,
                      key: '❤️',
                    },
                  },
                  created_at: msg.created_at,
                  sender_id: this.getUserId() || '',
                  timestamp: new Date(msg.created_at).getTime(),
                  room_id: msg.post_id,
                } as IRoomEvent);
              }
            });

            return [...newReactionEvents];
          };
          const mapperMessageResponse = (messages: Comment[]): SlidingSyncResponse['rooms'][string]['timeline'] => {
            return messages.map((msg) => {
              // A Event is Root Thread if:
              //  - It is not a reply to another comment
              //  - It has replies
              //  - Not have parent_comment_id
              // If just condition 1 (not reply to another comment), it is just a normal comment in a mainline. And its not a thread root.

              const normalEventInThreadContent = msg.parent_comment_id
                && {
                  'm.relates_to': {
                    rel_type: 'm.thread',
                    event_id: msg.parent_comment_id,
                    // If this comment is a reply to another comment and this not the parent comment
                    ...(msg.reply_to_comment_id && msg.reply_to_comment_id !== msg.parent_comment_id
                      && { 'm.in_reply_to': { event_id: msg.reply_to_comment_id } }
                    ),
                  },
                };

              return {
                type: 'm.room.message',
                sender_id: msg.commenter_id,
                content: {
                  text: msg.content,
                  msgtype: MsgType.Text,
                  ...normalEventInThreadContent,
                },
                state_key: undefined,
                id: msg.id,
                timestamp: new Date(msg.updated_at).getTime(),
                event_id: msg.id,
                origin_server_ts: new Date(msg.updated_at).getTime(),
                room_id: msg.post_id,
                created_at: msg.created_at,
                unsigned: {
                  'm.relations': {
                    ...(msg.likes && { 'm.annotation': {
                      chunk: [{ // no need to fill this out - This field is some react sample data
                        type: 'm.reaction',
                        sender: new Date().getTime().toString() || '',
                        content: { 'm.relates_to': { key: '❤️' } },
                      }],
                      count: msg.likes || 0,
                      limited: true,
                    } }),
                  },

                },
                time_pin: msg.time_pin,
              };
            });
          };
          const mapped = mapperMessageResponse(msgs);
          const xhubChatEvents = [...mapped, ...appendLikedByMe(msgs)].map(this.getEventMapper());

          // Process latest events first
          for (const event of xhubChatEvents.slice().reverse()) {
            await thread?.processEvent(event);
            const sender = event.getSender()!;
            if (!backwards || thread?.getEventReadUpTo(sender) === null) {
              room.addLocalEchoReceipt(sender, event, ReceiptType.Read);
            }
          }

          const newToken = _res.data.next_cursor || null;

          const timelineSet = eventTimeline.getTimelineSet();
          timelineSet.addEventsToTimeline(xhubChatEvents, backwards, false, eventTimeline, newToken ?? null);

          if (!newToken && backwards) {
            const originalEvent
              = thread.rootEvent
                ?? mapper(await this.fetchRoomEvent(room.roomData.category, thread.id));
            timelineSet.addEventsToTimeline([originalEvent], true, false, eventTimeline, null);
          }

          this.processAggregatedTimelineEvents(timelineSet.room, xhubChatEvents);

          // if we've hit the end of the timeline, we need to stop trying to
          // paginate. We need to keep the 'forwards' token though, to make sure
          // we can recover from gappy syncs.
          if (backwards && !newToken) {
            eventTimeline.setPaginationToken(null, dir);
          }

          return Boolean(newToken);
        })
        .finally(() => {
          eventTimeline.paginationRequests[dir] = null;
        });
      eventTimeline.paginationRequests[dir] = promise;

      return promise;
    } else {
      if (!room) throw new Error(`Unknown room ${eventTimeline.getRoomId()}`);

      if (room.roomData.category === RoomCategory.ChatRoom) {
        const promise = this.createMessagesRequest(eventTimeline.getRoomId()!, token, opts.limit, dir, opts.firstLoad)
          .then((res) => {
            // reverse to make them chronological
            const msgs = res.data.messages.reverse() || [];
            const token = msgs.length ? msgs[0].created_at : undefined;

            const mapperMessageResponse = (messages: IEvent[]): SlidingSyncResponse['rooms'][string]['timeline'] => {
              return messages.map(msg => ({
                type: 'm.room.message',
                sender_id: msg.sender_id,
                content: {
                  text: msg.content,
                  msgtype: MsgType.Text,
                },
                state_key: undefined,
                id: msg.id,
                timestamp: msg.timestamp,
                event_id: msg.id,
                origin_server_ts: new Date(msg.timestamp).getTime(),
                room_id: msg.room_id,
                created_at: msg.created_at,
              }));
            };
            const xhubChatEvents = mapperMessageResponse(msgs).map(this.getEventMapper());

            const timelineSet = eventTimeline.getTimelineSet();
            const [timelineEvents, , unknownRelations] = room.partitionThreadedEvents(xhubChatEvents);
            timelineSet.addEventsToTimeline(xhubChatEvents, backwards, false, eventTimeline, token);
            this.processAggregatedTimelineEvents(room, timelineEvents);
            this.processThreadRoots(
              room,
              timelineEvents.filter(it => it.getServerAggregatedRelation(THREAD_RELATION_TYPE.name)),
              false,
            );
            unknownRelations.forEach(event => room.relations.aggregateChildEvent(event));

            //  Have 2 cases to know if we are at the end of the timeline:
            //  - result.length < limit (implying there are no more events to fetch)
            //  - if call api returns no events at all
            const atEnd = msgs.length < (opts.limit || 30);
            // if we've hit the end of the timeline, we need to stop trying to
            // paginate. We need to keep the 'forwards' token though, to make sure
            // we can recover from gappy syncs.
            if (backwards && atEnd) {
              eventTimeline.setPaginationToken(null, dir);
            }
            return !atEnd;
          })
          .finally(() => {
            eventTimeline.paginationRequests[dir] = null;
          });
        eventTimeline.paginationRequests[dir] = promise;

        return promise;
      } else {
        const promise = this.createCommentsRequest(eventTimeline.getRoomId()!, {
          limit: opts.limit || 30,
          cursor: token?.includes('_post_content') ? undefined : token,
        })
          .then((res) => {
            const msgs = res.data.comments || [];
            const token = res.data.next_cursor || null;
            this.updateRoomMemberWhenFetchFromServer(msgs, eventTimeline);

            const appendLikedByMe = (msgs: Comment[]): (IRoomEvent | IStateEvent)[] => {
              const newReactionEvents: IRoomEvent[] = [];
              msgs.forEach((msg) => {
                if (msg.liked) {
                  newReactionEvents.push({
                    type: EventType.Reaction,
                    id: `${msg.id}_comment_like`,
                    content: {
                      'm.relates_to': {
                        rel_type: 'm.annotation',
                        event_id: `${msg.id}`,
                        key: '❤️',
                      },
                    },
                    created_at: msg.created_at,
                    sender_id: this.getUserId() || '',
                    timestamp: new Date(msg.created_at).getTime(),
                    room_id: msg.post_id,
                  } as IRoomEvent);
                }
              });

              return [...newReactionEvents];
            };
            const mapped = this.mapperCommentResponse(msgs);
            const xhubChatEvents = [...mapped, ...appendLikedByMe(msgs)].map(this.getEventMapper());

            const timelineSet = eventTimeline.getTimelineSet();
            const [timelineEvents, , unknownRelations] = room.partitionThreadedEvents(xhubChatEvents);
            timelineSet.addEventsToTimeline(xhubChatEvents, backwards, false, eventTimeline, token);
            this.processAggregatedTimelineEvents(room, timelineEvents);
            this.processThreadRoots(
              room,
              timelineEvents.filter(it => it.getServerAggregatedRelation(THREAD_RELATION_TYPE.name)),
              false,
            );
            unknownRelations.forEach(event => room.relations.aggregateChildEvent(event));

            //  Have 2 cases to know if we are at the end of the timeline:
            //  - result.length < limit (implying there are no more events to fetch)
            //  - if call api returns no events at all
            const atEnd = msgs.length < (opts.limit || 30) && !res.data.next_cursor;
            // if we've hit the end of the timeline, we need to stop trying to
            // paginate. We need to keep the 'forwards' token though, to make sure
            // we can recover from gappy syncs.
            if (backwards && atEnd) {
              eventTimeline.setPaginationToken(null, dir);
            }
            return !atEnd;
          })
          .finally(() => {
            eventTimeline.paginationRequests[dir] = null;
          });
        eventTimeline.paginationRequests[dir] = promise;

        return promise;
      }
    }
  }

  private updateRoomMemberWhenFetchFromServer(comments: Comment[], eventTimeline: EventTimeline, dir?: Direction): void {
    const members: string[] = [];
    const stateEvents: IStateEvent[] = comments.map((ev) => {
      if (members.includes(ev.commenter.id)) {
        return undefined;
      }
      members.push(ev.commenter.id);
      const rs: IStateEvent = {
        state_key: ev.commenter.id,
        type: 'm.room.member',
        created_at: ev.created_at,
        sender_id: ev.commenter.id,
        timestamp: new Date(ev.created_at).getTime(),
        id: `${ev.id}_member_${ev.commenter.id}`,
        room_id: ev.post_id,
        content: {
          membership: KnownMembership.Join,
          avatar_url: ev.commenter.avatar,
          displayname: `${ev.commenter.first_name} ${ev.commenter.last_name}`,
        },
        unsigned: {
          age: 0,
        },
      };

      return rs;
    }).filter(ev => !!ev);

    if (dir) {
      const roomState = eventTimeline.getState(dir)!;
      roomState.setUnknownStateEvents(stateEvents.map(this.getEventMapper()));
    } else {
      const roomState = eventTimeline.getState(EventTimeline.FORWARDS)!;
      roomState.setUnknownStateEvents(stateEvents.map(this.getEventMapper()));

      const roomStateBack = eventTimeline.getState(EventTimeline.BACKWARDS)!;
      roomStateBack.setUnknownStateEvents(stateEvents.map(this.getEventMapper()));
    }
  }

  /**
   * Get the events in a thread
   * @param eventId - The event ID of the thread root
   * @returns A promise which resolves to the list of comments in the thread
   */
  private fetchEventsInThread(eventId: string, opts?: { cursor?: string; limit?: number }): Promise<HttpResponse<ResponseCursor<Comment[], 'comments'>>> {
    return this.http.authedRequest(Method.Get, `/comments/by-ids`, {
      ids: [eventId],
      ...opts,
    });
  }

  /**
   * Processes a list of thread roots and creates a thread model
   * @param room - the room to create the threads in
   * @param threadedEvents - an array of thread roots
   * @param toStartOfTimeline - the direction
   */
  public processThreadRoots(room: Room, threadedEvents: XHubChatEvent[], toStartOfTimeline: boolean): void {
    if (!this.supportsThreads()) return;
    room.processThreadRoots(threadedEvents, toStartOfTimeline);
  }

  protected updatePendingEventStatus(room: Room | null, event: XHubChatEvent, newStatus: EventStatus): void {
    if (room) {
      room.updatePendingEvent(event, newStatus);
    } else {
      event.setStatus(newStatus);
    }
  }

  /**
   * encrypts the event if necessary; adds the event to the queue, or sends it; marks the event as sent/unsent
   * @returns returns a promise which resolves with the result of the send request
   */
  protected async encryptAndSendEvent(room: Room | null, event: XHubChatEvent): Promise<ISendEventResponse>;
  /**
   * Simply sends a delayed event without encrypting it.
   * TODO: Allow encrypted delayed events, and encrypt them properly
   * @param delayOpts - Properties of the delay for this event.
   * @returns returns a promise which resolves with the result of the delayed send request
   */
  protected async encryptAndSendEvent(room: Room | null, event: XHubChatEvent, delayOpts: SendDelayedEventRequestOpts): Promise<SendDelayedEventResponse>;
  protected async encryptAndSendEvent(
    room: Room | null,
    event: XHubChatEvent,
    delayOpts?: SendDelayedEventRequestOpts,
  ): Promise<ISendEventResponse | SendDelayedEventResponse> {
    if (delayOpts) return this.sendEventHttpRequest(event, delayOpts);

    try {
      try {
        await this.encryptEventIfNeeded(event, room ?? undefined);
      } catch (e) {
        this.logger.error('Error encrypting event', e);
      }

      let promise: Promise<ISendEventResponse> | null = null;
      if (this.scheduler) {
        // if this returns a promise then the scheduler has control now and will
        // resolve/reject when it is done. Internally, the scheduler will invoke
        // path is executed regardless.
        promise = this.scheduler.queueEvent(event);
        if (promise && this.scheduler.getQueueForEvent(event)!.length > 1) {
          // event is processed FIFO so if the length is 2 or more we know
          // this event is stuck behind an earlier event.
          this.updatePendingEventStatus(room, event, EventStatus.QUEUED);
        }
      }

      if (!promise) {
        promise = this.sendEventHttpRequest(event);
        if (room) {
          promise = promise.then((res) => {
            room.updatePendingEvent(event, EventStatus.SENT, res.data.event_id);
            return res;
          });
        }
      }

      return await promise;
    } catch (err) {
      this.logger.error('Error sending event', err);
      try {
        // set the error on the event before we update the status:
        // updating the status emits the event, so the state should be
        // consistent at that point.
        event.error = <XHubChatError>err;
        this.updatePendingEventStatus(room, event, EventStatus.NOT_SENT);
      } catch (e) {
        this.logger.error('Exception in error handler!', e);
      }
      if (err instanceof XHubChatError) err.event = event;
      throw err;
    }
  }

  /**
   * Resend an event. Will also retry any to-device messages waiting to be sent.
   * @param event - The event to resend.
   * @param room - Optional. The room the event is in. Will update the
   * timeline entry if provided.
   * @returns Promise which Resolves: to an ISendEventResponse object
   *                        Rejects: with an error response.
   */
  public resendEvent(event: XHubChatEvent, room: Room): Promise<ISendEventResponse> {
    logger.trace(`Resending event ${event.getId()} in room ${room.roomId}`);
    this.updatePendingEventStatus(room, event, EventStatus.SENDING);
    return this.encryptAndSendEvent(room, event);
  }

  private sendEventHttpRequest(event: XHubChatEvent): Promise<ISendEventResponse>;
  private sendEventHttpRequest(event: XHubChatEvent, delayOpts: SendDelayedEventRequestOpts): Promise<SendDelayedEventResponse>;
  private async sendEventHttpRequest(event: XHubChatEvent):
  Promise<ISendEventResponse | SendDelayedEventResponse | HttpResponse<Comment>> {
    const room = this.getRoom(event.getRoomId()!);
    // Send message to Chat Room
    if (room?.roomData.category === RoomCategory.ChatRoom) {
      let txnId = event.getTxnId();
      if (!txnId) {
        txnId = this.makeTxnId();
        event.setTxnId(txnId);
      }

      const pathParams = { $txnId: txnId };

      let path: string;

      if (event.isState()) {
        let pathTemplate = '/messages/send';
        if (event.getStateKey() && event.getStateKey()!.length > 0) {
          pathTemplate = '/messages/send';
        }
        path = encodeUri(pathTemplate, pathParams);
      } else {
        path = encodeUri('/messages/send', pathParams);
      }

      const content = event.getWireContent();
      return this.http.authedRequest<ISendEventResponse>(Method.Post, path, undefined, {
        key: txnId,
        message: content.text || '',
        room_chat_id: event.getRoomId()!,
        type_message: 'text',
      });
    } else {
      const relates_to = event.getContent()['m.relates_to'];
      // If event is relation, and rel_type is m.annotation (reaction)
      // then handle it in Post
      if (relates_to && event.isRelation() && relates_to.rel_type === 'm.annotation') {
        if (relates_to.event_id?.includes('_post_content')) { // Like a post
          return this.likePost(relates_to.event_id.split('_post_content')[0]);
        } else {
          if (relates_to.event_id === undefined) {
            throw new Error('Event ID is undefined in relation content for annotation');
          }
          return this.likeComment(relates_to.event_id);
        }
      } else if (event.isRedaction() && event.event.redacts) {
        // Unlike post or comment
        if (event.event.redacts?.includes('_post_like')) { // Like a post
          return this.unlikePost(event.event.redacts.split('_post_like')[0]);
        } else {
          if (event.event.redacts === undefined) {
            throw new Error('Event ID is undefined in relation content for annotation');
          }
          return this.unlikeComment(event.event.redacts.split('_comment_like')[0]);
        }
      }

      // send comment
      const content = event.getWireContent();

      return this.http.authedRequest<HttpResponse<Comment>>(Method.Post, '/comments', undefined, {
        content: content.text || '',
        parent_comment_id: content['m.relates_to']?.event_id,
        reply_to_comment_id: content['m.relates_to']?.event_id === event.replyEventId
          ? undefined
          : event.replyEventId,
        post_id: event.getRoomId()!,
      });
    }
  }

  /**
   * @param txnId - Optional.
   * @returns Promise which resolves: to an ISendEventResponse object
   * @returns Rejects: with an error response.
   */
  public sendMessage(roomId: string, content: RoomMessageEventContent, txnId?: string): Promise<ISendEventResponse>;
  public sendMessage(roomId: string, threadId: string | null, content: RoomMessageEventContent, txnId?: string): Promise<ISendEventResponse>;
  public sendMessage(
    roomId: string,
    threadId: string | null | RoomMessageEventContent,
    content?: RoomMessageEventContent | string,
    txnId?: string,
  ): Promise<ISendEventResponse> {
    if (typeof threadId !== 'string' && threadId !== null) {
      txnId = content as string;
      content = threadId as RoomMessageEventContent;
      threadId = null;
    }

    const eventType = EventType.RoomMessage;
    const sendContent = content as RoomMessageEventContent;

    return this.sendEvent(roomId, threadId as string | null, eventType, sendContent, txnId);
  }

  public sendEvent<K extends keyof TimelineEvents>(
    roomId: string,
    eventType: K,
    content: TimelineEvents[K],
    txnId?: string,
  ): Promise<ISendEventResponse>;
  public sendEvent<K extends keyof TimelineEvents>(
    roomId: string,
    threadId: string | null,
    eventType: K,
    content: TimelineEvents[K],
    txnId?: string,
  ): Promise<ISendEventResponse>;
  public sendEvent(
    roomId: string,
    threadIdOrEventType: string | null,
    eventTypeOrContent: string | IContent,
    contentOrTxnId?: IContent | string,
    txnIdOrVoid?: string,
  ): Promise<ISendEventResponse> {
    let threadId: string | null;
    let eventType: string;
    let content: IContent;
    let txnId: string | undefined;
    // `THREAD_PREFIX` là hằng số '$', dùng để nhận diện một event ID (Thread roomID).
    if (!threadIdOrEventType?.startsWith(THREAD_PREFIX) && threadIdOrEventType !== null) {
      // TRƯỜNG HỢP 1: Gửi event vào timeline chính. (không phải thread).
      // Tham số thứ hai (threadIdOrEventType) không phải là một event ID (vì không bắt đầu bằng '$').
      // Do đó, nó phải là `eventType`.
      txnId = contentOrTxnId as string;
      content = eventTypeOrContent as IContent;
      eventType = threadIdOrEventType;
      threadId = null;
    } else {
      // TRƯỜNG HỢP 2: Gửi event vào một thread.
      // Tham số thứ hai (threadIdOrEventType) là một event ID (bắt đầu bằng '$') hoặc là `null`.
      // Do đó, nó là `threadId`.
      txnId = txnIdOrVoid;
      content = contentOrTxnId as IContent;
      eventType = eventTypeOrContent as string;
      // ! Need to remove THREAD_PREFIX from threadId, because THREAD_PREFIX is just a marker to identify threadId, server doesn't need it.
      threadId = threadIdOrEventType?.replace(THREAD_PREFIX, '') || null;
    }

    this.addThreadRelationIfNeeded(content, threadId, roomId);
    return this.sendCompleteEvent(roomId, threadId, { type: eventType, content }, txnId);
  }

  /**
   * Send a receipt.
   * @param event - The event being acknowledged
   * @param receiptType - The kind of receipt e.g. "m.read". Other than
   * ReceiptType.Read are experimental!
   * @param body - Additional content to send alongside the receipt.
   * @param unthreaded - An unthreaded receipt will clear room+thread notifications
   * @returns Promise which resolves: to an empty object `{}`
   * @returns Rejects: with an error response.
   */
  public async sendReceipt(
    event: XHubChatEvent,
    receiptType: ReceiptType,
    body?: Record<string, any>,
    unthreaded = false,
  ): Promise<EmptyObject> {
    const path = encodeUri('/rooms/$roomId/receipt/$receiptType/$eventId', {
      $roomId: event.getRoomId()!,
      $receiptType: receiptType,
      $eventId: event.getId()!,
    });

    // Unless we're explicitly making an unthreaded receipt or we don't
    // support threads, include the `thread_id` property in the body.
    const shouldAddThreadId = !unthreaded && this.supportsThreads();
    const fullBody = shouldAddThreadId ? { ...body, thread_id: threadIdForReceipt(event) } : body;

    const promise = this.http.authedRequest<EmptyObject>(Method.Post, path, undefined, fullBody || {});

    const room = this.getRoom(event.getRoomId());
    if (room && this.credentials.userId) {
      room.addLocalEchoReceipt(this.credentials.userId, event, receiptType, unthreaded);
    }
    return promise;
  }

  /**
   * Send a read receipt.
   * @param event - The event that has been read.
   * @param receiptType - other than ReceiptType.Read are experimental! Optional.
   * @returns Promise which resolves: to an empty object `{}`
   * @returns Rejects: with an error response.
   */
  public async sendReadReceipt(
    event: XHubChatEvent | null,
    receiptType = ReceiptType.Read,
    unthreaded = false,
  ): Promise<EmptyObject | undefined> {
    if (!event) return;
    const eventId = event.getId()!;
    const room = this.getRoom(event.getRoomId());
    if (room?.hasPendingEvent(eventId)) {
      throw new Error(`Cannot set read receipt to a pending event (${eventId})`);
    }

    return this.sendReceipt(event, receiptType, {}, unthreaded);
  }

  /**
   * @param txnId -  transaction id. One will be made up if not supplied.
   * @param opts - Redact options
   * @returns Promise which resolves: TODO
   * @returns Rejects: with an error response.
   * @throws Error if called with `with_rel_types` (MSC3912) but the server does not support it.
   *         Callers should check whether the server supports MSC3912 via `MatrixClient.canSupport`.
   */
  public redactEvent(
    roomId: string,
    eventId: string,
    txnId?: string | undefined,
    opts?: IRedactOpts,
  ): Promise<ISendEventResponse>;
  public redactEvent(
    roomId: string,
    threadId: string | null,
    eventId: string,
    txnId?: string | undefined,
    opts?: IRedactOpts,
  ): Promise<ISendEventResponse>;
  public redactEvent(
    roomId: string,
    threadId: string | null,
    eventId?: string,
    txnId?: string | IRedactOpts,
    opts?: IRedactOpts,
  ): Promise<ISendEventResponse> {
    if (!eventId?.startsWith(THREAD_PREFIX)) {
      opts = txnId as IRedactOpts;
      txnId = eventId;
      eventId = threadId!;
      threadId = null;
    }
    const reason = opts?.reason;
    const content: IContent = { reason };

    return this.sendCompleteEvent(
      roomId,
      threadId,
      {
        type: EventType.RoomRedaction,
        content,
        redacts: eventId,
      },
      txnId as string,
    );
  }

  /**
   * @param txnId - Optional.
   * @returns
   * @returns Rejects: with an error response.
   */
  public sendTextMessage(roomId: string, body: string, txnId?: string): Promise<ISendEventResponse>;
  public sendTextMessage(roomId: string, threadId: string | null, body: string, txnId?: string): Promise<ISendEventResponse>;
  public sendTextMessage(roomId: string, threadId: string | null, body: string, txnId?: string): Promise<ISendEventResponse> {
    if (!threadId?.startsWith(THREAD_PREFIX) && threadId !== null) {
      txnId = body;
      body = threadId;
      threadId = null;
    }
    const content = makeTextMessage(body);
    return this.sendMessage(roomId, threadId, content, txnId);
  }

  /**
   * @param eventObject - An object with the partial structure of an event, to which event_id, user_id, room_id and origin_server_ts will be added.
   * @param txnId - Optional.
   * @returns Promise which resolves: to an empty object `{}`
   * @returns Rejects: with an error response.
   */
  private sendCompleteEvent(roomId: string, threadId: string | null, eventObject: Partial<IEvent>, txnId?: string): Promise<ISendEventResponse>;
  /**
   * Sends a delayed event (MSC4140).
   * @param eventObject - An object with the partial structure of an event, to which event_id, user_id, room_id and origin_server_ts will be added.
   * @param delayOpts - Properties of the delay for this event.
   * @param txnId - Optional.
   * @returns Promise which resolves: to an empty object `{}`
   * @returns Rejects: with an error response.
   */
  private sendCompleteEvent(roomId: string, threadId: string | null, eventObject: Partial<IEvent>, delayOpts: SendDelayedEventRequestOpts, txnId?: string): Promise<SendDelayedEventResponse>;
  private sendCompleteEvent(
    roomId: string,
    threadId: string | null,
    eventObject: Partial<IEvent>,
    delayOptsOrTxnId?: SendDelayedEventRequestOpts | string,
    txnIdOrVoid?: string,
  ): Promise<ISendEventResponse | SendDelayedEventResponse> {
    let delayOpts: SendDelayedEventRequestOpts | undefined;
    let txnId: string | undefined;
    if (typeof delayOptsOrTxnId === 'string') {
      txnId = delayOptsOrTxnId;
    } else {
      delayOpts = delayOptsOrTxnId;
      txnId = txnIdOrVoid;
    }

    if (!txnId) {
      txnId = this.makeTxnId();
    }

    // We always construct a XHubChatEvent when sending because the store and scheduler use them.
    // We'll extract the params back out if it turns out the client has no scheduler or store.
    const localEvent = new XHubChatEvent(
      Object.assign(eventObject, {
        event_id: `${txnId}`,
        id: `${txnId}`,
        user_id: this.credentials.userId,
        sender_id: this.credentials.userId,
        room_id: roomId,
        origin_server_ts: eventObject.timestamp || new Date().getTime(),
        timestamp: eventObject.timestamp || new Date().getTime(),
      } as Partial<IEvent>),
    );

    const room = this.getRoom(roomId);
    const thread = threadId ? room?.getThread(threadId) : undefined;
    if (thread) {
      localEvent.setThread(thread);
    }

    if (!delayOpts) {
      // set up re-emitter for this new event - this is normally the job of EventMapper but we don't use it here
      room?.reEmitter.reEmit(localEvent, [XHubChatEventEvent.BeforeRedaction]);
    }

    // if this is a relation or redaction of an event
    // that hasn't been sent yet (e.g. with a local id starting with a ~)
    // then listen for the remote echo of that event so that by the time
    // this event does get sent, we have the correct event_id
    const targetId = localEvent.getAssociatedId();
    if (targetId?.startsWith('xh')) {
      const target = room?.getPendingEvents().find(e => e.getId() === targetId);
      target?.once(XHubChatEventEvent.LocalEventIdReplaced, () => {
        localEvent.updateAssociatedId(target.getId()!);
      });
    }

    const type = localEvent.getType();
    this.logger.debug(
      `sendEvent of type ${type} in ${roomId} with txnId ${txnId}${delayOpts ? ' (delayed event)' : ''}`,
    );

    localEvent.setTxnId(txnId);
    localEvent.setStatus(EventStatus.SENDING);

    // TODO: separate store for delayed events?
    if (!delayOpts) {
      // add this event immediately to the local store as 'sending'.
      room?.addPendingEvent(localEvent, txnId);

      // addPendingEvent can change the state to NOT_SENT if it believes
      // that there's other events that have failed. We won't bother to
      // try sending the event if the state has changed as such.
      if (localEvent.status === EventStatus.NOT_SENT) {
        return Promise.reject(new Error('Event blocked by other events not yet sent'));
      }

      // Make http request to send the event.
      return this.encryptAndSendEvent(room, localEvent);
    } else {
      // Make http request to send the event.
      return this.encryptAndSendEvent(room, localEvent, delayOpts);
    }
  }

  private async unlikeComment(commentId: string): Promise<ISendEventResponse> {
    return await this.http.authedRequest<HttpResponse<LikeCommentResponse>>(
      Method.Post,
      `/comments/${commentId}/unlike`,
      undefined,
    ).then(res => ({
      code: res.code,
      data: {
        event_id: `${res.data.id}_comment_unlike`,
        id: `${res.data.id}_comment_unlike`,
        post_id: res.data.post_id,
      },
      message: res.message,
      success: res.success,
    } as ISendEventResponse));
  }

  private async likeComment(commentId: string): Promise<ISendEventResponse> {
    return await this.http.authedRequest<HttpResponse<LikeCommentResponse>>(
      Method.Post,
      `/comments/${commentId}/like`,
      undefined,
    ).then(res => ({
      code: res.code,
      data: {
        event_id: `${res.data.id}_comment_like`,
        id: `${res.data.id}_comment_like`,
        post_id: res.data.post_id,
      },
      message: res.message,
      success: res.success,
    } as ISendEventResponse));
  }

  /**
   * Make a request to call to server like a post
   * @param postId - The post ID
   * @returns A promise which resolves to ISendEventResponse
   */
  private async likePost(postId: string): Promise<ISendEventResponse> {
    return await this.http.authedRequest<HttpResponse<LikePostResponse>>(
      Method.Post,
      `/posts/${postId}/like`,
      undefined,
    ).then(res => ({
      code: res.code,
      data: {
        event_id: `${res.data.id}_post_like`,
        id: `${res.data.id}_post_like`,
      },
      message: res.message,
      success: res.success,
    } as ISendEventResponse));
  }

  /**
   * Make a request to call to server unlike a post
   * @param postId - The post ID
   * @returns Promise which resolves to ISendEventResponse
   */
  private async unlikePost(postId: string): Promise<ISendEventResponse> {
    return await this.http.authedRequest<HttpResponse<LikePostResponse>>(
      Method.Post,
      `/posts/${postId}/unlike`,
      undefined,
    ).then(res => ({
      code: res.code,
      data: {
        event_id: `${res.data.id}_post_unlike`,
        id: `${res.data.id}_post_unlike`,
      },
      message: res.message,
      success: res.success,
    } as ISendEventResponse));
  }

  /**
   * If we expect that an event is part of a thread but is missing the relation
   * we need to add it manually, as well as the reply fallback
   */
  private addThreadRelationIfNeeded(content: IContent, threadId: string | null, roomId: string): void {
    if (threadId && !content['m.relates_to']?.rel_type) {
      const isReply = !!content['m.relates_to']?.['m.in_reply_to'];
      content['m.relates_to'] = {
        ...content['m.relates_to'],
        rel_type: THREAD_RELATION_TYPE.name,
        event_id: threadId,
        // Set is_falling_back to true unless this is actually intended to be a reply
        is_falling_back: !isReply,
      };
      const thread = this.getRoom(roomId)?.getThread(threadId);
      if (thread && !isReply) {
        content['m.relates_to']['m.in_reply_to'] = {
          event_id:
              thread
                .lastReply((ev: XHubChatEvent) => {
                  return ev.isRelation(THREAD_RELATION_TYPE.name) && !ev.status;
                })
                ?.getId() ?? threadId,
        };
      }
    }
  }

  /**
   * Makes a request to /messages with the appropriate lazy loading filter set.
   * XXX: if we do get rid of scrollback (as it's not used at the moment),
   * we could inline this method again in paginateEventTimeline as that would
   * then be the only call-site
   * @param roomId - the room to get events from
   * @param fromToken - the token to get events from
   * @param limit - the maximum amount of events the retrieve
   * @param dir - 'f' or 'b'
   * @param firstLoad - Flag indicates whether it's the first load of the room or not.
   *
   * @deprecated - Don't use
   */
  // XXX: Intended private, used in code.
  public createMessagesRequest(
    roomId: string,
    fromToken: string | null,
    limit = 30,
    dir: Direction,
    /**
     * We don't fetch message of room when first loading room list to save bandwidth.
     * Messages of the room just will be fetched when the room is opened for
     * the first time. (when client wants)
     * Then this flag indicates whether it's the first load of the room or not.
     */
    firstLoad = false,
  ): Promise<HttpResponse<ServerMessageRes>> {
    const path = encodeUri('/messages/room/$roomId', { $roomId: roomId });

    const params: Record<string, string> = {
      limit: limit.toString(),
      dir,
      room_id: roomId,
    };

    // If this call from first load, we don't set fromToken
    // to avoid fetching messages with cursor and can get all new messages.
    if (fromToken && !firstLoad) {
      params.last_time_message = fromToken;
    }
    return this.http.authedRequest(Method.Get, path, params);
  }

  /**
   * Makes a request to /messages with the appropriate lazy loading filter set.
   * XXX: if we do get rid of scrollback (as it's not used at the moment),
   * we could inline this method again in paginateEventTimeline as that would
   * then be the only call-site
   * @param roomId - the room to get events from
   * @param opts - options for the request
   * @param opts.limit - the maximum amount of events the retrieve
   * @param opts.cursor - the cursor to get events from
   */
  // XXX: Intended private, used in code.
  public createCommentsRequest(roomId: string, { limit, cursor }: { limit: number; cursor?: string | null }): Promise<HttpResponse<ServerCommentRes>> {
    const path = encodeUri('/posts/$roomId/comments', { $roomId: roomId });

    const params: Record<string, string> = {
      limit: limit.toString(),
    };
    if (cursor) {
      params.cursor = cursor;
    }
    return this.http.authedRequest(Method.Get, path, params);
  }

  /**
   * A helper to determine thread support client
   * @returns a boolean to determine if threads are enabled
   */
  public supportsThreads(): boolean {
    return this.clientOpts?.threadSupport || false;
  }

  /**
   * Get the user-id of the logged-in user
   *
   * @returns ID for the logged-in user, or null if not logged in
   */
  public getUserId(): string | null {
    return this.credentials?.userId ?? null;
  }

  /**
   * Get the room for the given room ID.
   * This function will return a valid room for any room for which a Room event
   * has been emitted. Note in particular that other events, eg. RoomState.members
   * will be emitted for a room before this function will return the given room.
   * @param roomId - The room ID
   * @returns The Room or null if it doesn't exist or there is no data store.
   */
  public getRoom(roomId: string | undefined): Room | null {
    return roomId ? this.store.getRoom(roomId) : null;
  }

  /**
   * Retrieve all known rooms.
   * @returns A list of rooms, or an empty list if there is no data store.
   */
  public getRooms(): Room[] {
    return this.store.getRooms();
  }

  /**
   * Get the user-id of the logged-in user
   * @returns User ID for the logged-in user
   * @throws Error if not logged in
   */
  public getSafeUserId(): string {
    const userId = this.getUserId();
    if (!userId) throw new Error('Expected logged in user but found none.');
    return userId;
  }

  public getEventMapper(options?: MapperOpts): EventMapper {
    return eventMapperFor(this, options || {});
  }

  /**
   * Set account data event for the current user, without waiting for the remote echo.
   *
   * @param eventType - The event type
   * @param content - the contents object for the event
   */
  public setAccountDataRaw<K extends keyof AccountDataEvents>(
    eventType: K,
    content: AccountDataEvents[K] | Record<string, never>,
  ): Promise<EmptyObject> {
    const path = encodeUri('/user/$userId/account_data/$type', {
      $userId: this.credentials.userId!,
      $type: eventType,
    });

    return this.http.authedRequest(Method.Put, path, undefined, content);
  }

  /**
   * Set account data event for the current user, and wait for the result to be echoed over `/sync`.
   *
   * Waiting for the remote echo ensures that a subsequent call to {@link getAccountData} will return the updated
   * value.
   *
   * If called before the client is started with {@link startClient}, logs a warning and falls back to
   * {@link setAccountDataRaw}.
   *
   * Retries the request up to 5 times in the case of an {@link ConnectionError}.
   *
   * @param eventType - The event type
   * @param content - the contents object for the event
   */
  public async setAccountData<K extends keyof AccountDataEvents>(
    eventType: K,
    content: AccountDataEvents[K] | Record<string, never>,
  ): Promise<EmptyObject> {
    // If the sync loop is not running, fall back to setAccountDataRaw.
    if (!this.clientRunning) {
      this.logger.warn(
        'Calling `setAccountData` before the client is started: `getAccountData` may return inconsistent results.',
      );
      return await retryNetworkOperation(5, () => this.setAccountDataRaw(eventType, content));
    }

    // If the account data is already correct, then we cannot expect an update over sync, and the operation
    // is, in any case, a no-op.
    //
    // NB that we rely on this operation being synchronous to avoid a race condition: there must be no `await`
    // between here and `this.addListener` below, in case we miss an update.
    const existingData = this.store.getAccountData(eventType);
    if (existingData && deepCompare(existingData.event.content, content)) return {};

    // Create a promise which will resolve when the update is received
    const updatedResolvers = Promise.withResolvers<void>();
    function accountDataListener(event: XHubChatEvent): void {
      // Note that we cannot safely check that the content matches what we expected, because there is a race:
      //   * We set the new content
      //   * Another client sets alternative content
      //   * Then /sync returns, but only reflects the latest content.
      //
      // Of course there is room for debate over what we should actually do in that case -- a subsequent
      // `getAccountData` isn't going to return the expected value, but whose fault is that? Databases are hard.
      //
      // Anyway, what we *shouldn't* do is get stuck in a loop. I think the best we can do is check that the event
      // type matches.
      if (event.getType() === eventType) updatedResolvers.resolve();
    }
    this.addListener(ClientEvent.AccountData, accountDataListener);

    try {
      const result = await retryNetworkOperation(5, () => this.setAccountDataRaw(eventType, content));
      await updatedResolvers.promise;
      return result;
    } finally {
      this.removeListener(ClientEvent.AccountData, accountDataListener);
    }
  }

  /**
   * Get account data event of given type for the current user.
   * @param eventType - The event type
   * @returns The contents of the given account data event
   */
  public getAccountData<K extends keyof AccountDataEvents>(eventType: K): XHubChatEvent | undefined {
    return this.store.getAccountData(eventType);
  }

  /**
   * Query the user directory with a term matching user IDs, display names and domains.
   * @param options
   * @param options.term - the term with which to search.
   * @param options.limit - the maximum number of results to return. The server will apply a limit if unspecified.
   * @returns Promise which resolves: an array of results.
   */
  public searchUserDirectory({ term, limit }: { term: string; limit?: number }): Promise<IUserDirectoryResponse> {
    const body: Body = {
      search_term: term,
    };

    if (limit !== undefined) {
      body.limit = limit;
    }

    return this.http.authedRequest(Method.Post, '/user_directory/search', undefined, body);
  }

  /**
   * Return the global notification EventTimelineSet, if any
   *
   * @returns the global notification EventTimelineSet
   */
  public getNotifTimelineSet(): EventTimelineSet | null {
    return this.notifTimelineSet;
  }

  /**
   * Retrieve all rooms that should be displayed to the user
   * This is essentially getRooms() with some rooms filtered out, eg. old versions
   * of rooms that have been replaced or (in future) other rooms that have been
   * marked at the protocol level as not to be displayed to the user.
   *
   * @param ProcessDynamicPredecessor - if true, look for an
   *                                           m.room.predecessor state event and
   *                                           use it if found (MSC3946).
   * @returns A list of rooms, or an empty list if there is no data store.
   */
  getVisibleRooms(ProcessDynamicPredecessor = false): Room[] {
    const allRooms = this.store.getRooms();

    const visibleRooms = new Set(allRooms);
    // Check condition after, now that we don't do unnecessary work
    return Array.from(visibleRooms);
  }

  /**
   * Return (and lazily create) a CentrifugeRealtime instance if realtime options were provided.
   * Does not auto-connect unless realtime.autoStart !== false and startClient() was called.
   */
  public getRealtime(): CentrifugeRealtime | undefined {
    if (!this.realtimeOpts) return undefined;
    if (!this._realtime) {
      this._realtime = new CentrifugeRealtime({
        ...this.realtimeOpts,
        logger: this.realtimeOpts.logger || this.logger,
      }, this);
    }
    return this._realtime;
  }

  /**
   * Get the current sync state.
   * @returns the sync state, which may be null.
   * @see XHubChatClient#event:"sync"
   */
  public getSyncState(): SyncState | null {
    return this.syncApi?.getSyncState() ?? null;
  }

  private async encryptEventIfNeeded(event: XHubChatEvent, room?: Room): Promise<void> {
    // Don't need to check, because encryption is not implemented yet
  }

  /**
   * Whether the initial sync has completed.
   * @returns True if at least one sync has happened.
   */
  public isInitialSyncComplete(): boolean {
    const state = this.getSyncState();
    if (!state) {
      return false;
    }
    return state === SyncState.Prepared || state === SyncState.Syncing;
  }

  /**
   * Attempts to decrypt an event
   * @param event - The event to decrypt
   * @returns A decryption promise
   */
  public decryptEventIfNeeded(event: XHubChatEvent): Promise<void> {
    return Promise.resolve();
  }

  /**
   * Get an event in a room by its event id.
   * @param roomCategory - The room category to fetch the event from.
   * @param eventId - The event id to fetch.
   * @returns Promise which resolves to an object containing the event.
   *          Rejects: with an error response.
   *
   * For now, use fetch event from point with limit 1 to get the event context
   * Because there is no dedicated endpoint to fetch a single event by its ID
   * - Call forward messages to get the neighboring events
   * - Call again with backward messages to get this event
   */
  public async fetchRoomEvent(roomCategory: RoomCategory, eventId: string): Promise<Partial<IEvent>> {
    if (roomCategory === RoomCategory.ChatRoom) {
      const path = encodeUri('/messages/$eventId/detail', {
        $eventId: eventId,
      });

      return this.http.authedRequest(Method.Get, path);
    } else {
      return this.http
        .authedRequest<HttpResponse<Comment>>(Method.Get, `/comments/${eventId}`)
        .then(res => this.mapperCommentResponse([res.data])[0]);
    }
  }

  /**
   * Perform a single sliding sync request.
   * @param req - The request to make. ! type any now because the spec is not stable.
   * @param abortSignal - Optional signal to abort request mid-flight.
   * @returns The sliding sync response, or a standard error.
   * @throws on non 2xx status codes with an object with a field "httpStatus":number.
   */
  public fetchRooms(req: CursorParams, abortSignal?: AbortSignal): Promise<HttpResponse<RoomsResponse>> {
    const qps: QueryDict = {};
    if (req.cursor) {
      qps.cursor = req.cursor;
      delete req.cursor;
    }
    if (req.limit) {
      qps.limit = req.limit.toString();
      delete req.limit;
    }

    return this.http.authedRequest<HttpResponse<RoomsResponse>>(
      Method.Get,
      '/activities',
      qps,
      undefined,
      { abortSignal },
    );
  }

  /**
   * Gets the latest receipt for a given user in the room
   * @param userId - The id of the user for which we want the receipt
   * @param ignoreSynthesized - Whether to ignore synthesized receipts or not
   * @param receiptType - Optional. The type of the receipt we want to get
   * @returns the latest receipts of the chosen type for the chosen user
   */
  public getReadReceiptForUserId(
    userId: string,
    ignoreSynthesized = false,
    receiptType = ReceiptType.Read,
  ): WrappedReceipt | null {
    const [realReceipt, syntheticReceipt] = this.receipts.get(receiptType)?.get(userId) ?? [null, null];
    if (ignoreSynthesized) return realReceipt;
    return syntheticReceipt ?? realReceipt;
  }

  /**
   * Calls aggregation functions for event types that are aggregated
   * Polls and location beacons
   * @param room - room the events belong to
   * @param events - timeline events to be processed
   */
  public processAggregatedTimelineEvents(room?: Room, events?: XHubChatEvent[]): void {
    if (!events?.length) return;
    if (!room) return;

    room.getLiveTimeline().getState(EventTimeline.FORWARDS)?.processBeaconEvents(events, this);
  }

  /**
   * Set the global notification EventTimelineSet
   *
   */
  setNotifTimelineSet(set: EventTimelineSet): void {
    this.notifTimelineSet = set;
  }

  /**
   * Once the client has been initialized, we want to clear notifications we
   * know for a fact should be here.
   *
   * We consider a room or a thread as fully read if the current user has sent
   * the last event in the live timeline of that context and if the read receipt
   * we have on record matches.
   */
  private fixupRoomNotifications = (): void => {
    if (this.isInitialSyncComplete()) {
      const unreadRooms = (this.getRooms() ?? []).filter((room) => {
        return room.getUnreadNotificationCount(NotificationCountType.Total) > 0;
      });

      for (const room of unreadRooms) {
        const currentUserId = this.getSafeUserId();
        room.fixupNotifications(currentUserId);
      }

      this.off(ClientEvent.Sync, this.fixupRoomNotifications);
    }
  };

  /**
   * Make up a new transaction id
   * @returns a new, unique, transaction id
   */
  public makeTxnId(): string {
    return `xh${new Date().getTime()}.${this.txnCtr++}`;
  }

  /**
   * High level helper method to stop the client from polling and allow a
   * clean shutdown.
   */
  public stopClient(): void {
    if (!this.clientRunning) return; // already stopped
    this.logger.debug('stopping XHubChatClient');

    this.clientRunning = false;

    this.syncApi?.stop();
    this.syncApi = undefined;

    // Gracefully close realtime connection
    this._realtime?.disconnect();
  }

  /**
   * Retrieve a user.
   * @param userId - The user ID to retrieve.
   * @returns A user or null if there is no data store or the user does
   * not exist.
   */
  public getUser(userId: string): User | null {
    return this.store.getUser(userId);
  }

  public async fetchRoom(roomId: string): Promise<HttpResponse<RoomData>> {
    return this.http.authedRequest(Method.Get, `/activities/${roomId}`);
  }

  /**
   * Create a new chat room.
   * @param body - The request body
   * @param body.logo - The logo of the room
   * @param body.name - The name of the room
   * @param body.participants - The participants of the room
   * @returns Promise which resolves: true if the room was created successfully
   */
  public async createRoom(body: {
    logo: string;
    name: string;
    participants: string[];
  }): Promise<RoomData> {
    const path = encodeUri('/chat-rooms', {});
    return await this.http.authedRequest<HttpResponse<{
      created_at: string;
      id: string;
      logo: string;
      name: string;
      participants: string[];
      updated_at: string;
    }>>(Method.Post, path, undefined, body).then(async (res) => {
      const roomData = await this.fetchRoom(res.data.id);
      const resp = await this.convertServerResToSdkRes({ items: [roomData.data], has_next: false, next_cursor: null });

      if (resp) {
        await this.syncApi?.onRoomData(res.data.id, resp.rooms[res.data.id]);
      }
      return roomData.data;
    });
  }

  /**
   * Create a direct message room with a user.
   * If a direct room already exists with this user, it will be returned.
   *
   * @param channel_slug - The user ID to create a direct message with
   * @returns Promise which resolves to the Room object
   *
   * @example
   * ```typescript
   * const room = await client.createDirectRoom('user123');
   * await client.sendTextMessage(room.roomId, 'Hello!');
   * ```
   */
  public async createDirectRoom(channel_slug: string): Promise<Room> {
    try {
      this.logger.info(`Creating direct room with user ${channel_slug}`);

      // Check if direct room already exists
      const existingRooms = this.getRooms();
      const contactor = this.getContactsManager().getContact(channel_slug);
      const existingDirectRoom = existingRooms.find((room) => {
        // Skip non-chat rooms
        if (room.roomData.category === RoomCategory.Post) {
          return false;
        }

        // Check if room has exactly 2 members (current user + target user)
        const members = room.getJoinedMembers();
        if (members.length !== 2) {
          return false;
        }

        // Check if target user is a member
        return members.some(member => member.userId === channel_slug);
      });

      if (existingDirectRoom) {
        this.logger.debug(`Found existing direct room ${existingDirectRoom.roomId} with user ${channel_slug}`);
        return existingDirectRoom;
      }

      // Create new direct room
      this.logger.debug(`7. Creating new direct room with user ${channel_slug}`);
      const response = await this.createRoom({
        logo: contactor?.avatarUrl ?? '',
        name: contactor?.displayName ?? channel_slug,
        participants: [channel_slug, this.getUserId()!],
      });

      const roomId = response.id;

      // Get the room object
      const room = this.getRoom(roomId);
      if (!room) {
        throw new Error(`Failed to get created room ${roomId}`);
      }

      this.logger.debug(`Direct room ${roomId} created successfully with user ${channel_slug}`);
      return room;
    } catch (error) {
      this.logger.error(`Failed to create direct room with user ${channel_slug}`, error);
      throw error;
    }
  }

  /**
   * Logs out the current session.
   * Obviously, further calls that require authorisation should fail after this
   * method is called. The state of the MatrixClient object is not affected:
   * it is up to the caller to either reset or destroy the MatrixClient after
   * this method succeeds.
   * @param stopClient - whether to stop the client before calling /logout to prevent invalid token errors.
   * @returns Promise which resolves: On success, the empty object `{}`
   */
  public async logout(stopClient = false): Promise<EmptyObject> {
    if (stopClient) {
      this.stopClient();
      this.http.abort();
    }
    return {};
  }

  /**
   * Clear any data out of the persistent stores used by the client.
   *
   * @returns Promise which resolves when the stores have been cleared.
   */
  public async clearStores(): Promise<void> {
    if (this.clientRunning) throw new Error('Cannot clear stores while client is running');

    const promises: Promise<void>[] = [];

    promises.push(this.store.deleteAllData());

    return Promise.all(promises).then(); // .then to fix types
  }
}
